import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Router } from '@angular/router';
import { TaxonomyApiService } from '../../../../services/api-services/taxonomy-api.service';
import { TaxonomyService } from '../../../../services/taxonomy.service';
import { NotificationService } from '../../../../services/notification.service';
import { TaxonomyButtonComponent } from '../../taxonomy-button/taxonomy-button.component';

@Component({
  selector: 'app-general-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatOptionModule,
    TaxonomyButtonComponent,
  ],
  templateUrl: './general-settings.component.html',
  styleUrls: ['./general-settings.component.scss'],
})
export class GeneralSettingsComponent implements OnInit {
  private http = inject(HttpClient);

  taxonomyId: string = this.taxonomyService.selectedTaxonomy?.taxonomy_id;
  taxonomy: any = { name: '', description: '', queueGroups: [] };
  originalTaxonomy = { name: '', description: '', queueGroups: [] as string[] };

  queueGroups = new FormControl<string[]>([]);
  searchText: string = '';
  deleteInput: string = '';

  queueGroupsList: QueueGroup[] = [];
  selectedGroups: string[] = [];

  filteredQueueGroups: {
    selected: QueueGroup[];
    unselected: QueueGroup[];
  } = { selected: [], unselected: [] };

  constructor(
    private taxonomyApiService: TaxonomyApiService,
    public taxonomyService: TaxonomyService,
    private router: Router,
    public notificationService: NotificationService
  ) {}

  ngOnInit() {
    this.getTaxonomyDetails();
  }

  getTaxonomyDetails() {
    const body = { taxonomy_id: this.taxonomyId };
    this.taxonomyApiService.getSpecificTaxonomyV2ById(body).subscribe({
      next: (res) => {
        const data = res.data;
        this.taxonomy = {
          name: data.TaxonomyName,
          description: data.TaxonomyDescription,
          queueGroups: data.LinkedQueueGroups,
        };
        this.selectedGroups = data.LinkedQueueGroups || [];
        this.queueGroups.setValue(this.selectedGroups);
        this.originalTaxonomy = {
          name: data.TaxonomyName || '',
          description: data.TaxonomyDescription || '',
          queueGroups: [...(data.LinkedQueueGroups || [])],
        };
        this.getListOfQueueGroups();
      },
      error: (err) => console.error('Failed to fetch taxonomy', err),
    });
  }

  getListOfQueueGroups() {
    this.taxonomyApiService.getListOfQueueGroups({}).subscribe({
      next: (res) => {
        this.queueGroupsList = res?.data?.map((group: string) => ({
          name: group,
          selected: this.selectedGroups.includes(group),
        }));
        this.filterQueueGroups();
      },
      error: (err) => console.error('Failed to fetch queue groups', err),
    });
  }

  filterQueueGroups() {
    const search = this.searchText.toLowerCase();
    this.filteredQueueGroups.selected = this.queueGroupsList.filter(
      (g) => g.selected && g.name.toLowerCase().includes(search)
    );
    this.filteredQueueGroups.unselected = this.queueGroupsList.filter(
      (g) => !g.selected && g.name.toLowerCase().includes(search)
    );
  }

  onDropdownToggle(opened: boolean) {
    if (!opened) {
      this.searchText = '';
    }
    this.filterQueueGroups();
  }

  onGroupSelectChange(group: QueueGroup, event: Event) {
    event.stopPropagation();
    group.selected = !group.selected;
    this.updateSelections();
  }

  deselectOption(groupName: string) {
    const group = this.queueGroupsList.find((g) => g.name === groupName);
    if (group) group.selected = false;
    this.updateSelections();
  }

  private updateSelections() {
    this.selectedGroups = this.queueGroupsList.filter((g) => g.selected).map((g) => g.name);
    this.queueGroups.setValue(this.selectedGroups);
    this.filterQueueGroups();
  }

  showConfirmationPopup = false;

  confirmSaveChanges() {
    const payload = {
      taxonomy_name: this.taxonomy.name,
      taxonomy_id: this.taxonomyId,
      taxonomy_description: this.taxonomy.description,
      linked_queue_groups: this.queueGroups.value ?? [],
    };

    this.taxonomyApiService.updateTaxonomyV2(payload).subscribe({
      next: () => {
        this.notificationService.success('update taxonomy', 'Taxonomy Updated');
        this.router.navigate([`/taxonomy-dashboard/${this.taxonomyId}`]);
      },
      error: (err) => {
        console.error('Failed to update taxonomy', err);
        this.notificationService.error('update taxonomy', 'Taxonomy Updation Failed');
      },
    });
    this.showConfirmationPopup = false;
  }

  cancelSave() {
    this.showConfirmationPopup = false;
  }

  handleSaveChanges() {
    this.showConfirmationPopup = true;
  }

  handleCancel() {
    this.router.navigate([`/taxonomy-dashboard/${this.taxonomyId}`]);
  }

  handleDeleteTaxonomy(deleteInput: string) {
    if (deleteInput === this.taxonomy.name) {
      this.taxonomyApiService.deleteTaxonomyV2({ taxonomy_id: this.taxonomyId }).subscribe({
        next: () => {
          this.notificationService.success('delete taxonomy', 'Taxonomy Deleted');
          this.router.navigate(['/taxonomy-dashboard']);
        },
        error: (err) => {
          console.error('Failed to delete taxonomy', err);
          this.notificationService.error('delete taxonomy', 'Deletion Failed');
        },
      });
    } else {
      this.notificationService.error('delete taxonomy', 'Taxonomy name mismatch');
    }
  }

  isSaveEnabled(): boolean {
    const nameTrimmed = this.taxonomy.name?.trim();
    const descTrimmed = this.taxonomy.description?.trim();
    const groupChanged =
      JSON.stringify(this.originalTaxonomy.queueGroups.sort()) !== JSON.stringify(this.selectedGroups.sort());
    return (
      nameTrimmed &&
      descTrimmed &&
      (nameTrimmed !== this.originalTaxonomy.name || descTrimmed !== this.originalTaxonomy.description || groupChanged)
    );
  }

  isDeleteEnabled(): boolean {
    return this.deleteInput === this.taxonomy.name;
  }
}

interface QueueGroup {
  name: string;
  selected: boolean;
}
